import { createFileRoute } from "@tanstack/react-router";
import { redirect } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ShieldCheck, Upload, X, Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/use-auth";
import { getMyVerification, submitVerification } from "@/lib/chef-verification.functions";
import { validateUpload, acceptAttribute } from "@/lib/upload-limits";

export const Route = createFileRoute("/_authenticated/dashboard/verifica")({
  head: () => ({ meta: [{ title: "Verifica Chef — ChefConnect" }] }),
  beforeLoad: async () => {
    // Sprint 25.2 — admin non passa dal funnel chef.
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
    if (isAdmin) throw redirect({ to: "/dashboard/admin" });
  },
  component: VerificationPage,
});

type DocKind = "id_card" | "vat" | "haccp" | "certificate" | "diploma" | "other";
const KIND_LABEL: Record<DocKind, string> = {
  id_card: "Documento d'identità",
  vat: "Partita IVA",
  haccp: "Attestato HACCP",
  certificate: "Certificazione",
  diploma: "Diploma / Qualifica",
  other: "Altro",
};

function VerificationPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const getFn = useServerFn(getMyVerification);
  const submitFn = useServerFn(submitVerification);
  const verQ = useQuery({ queryKey: ["my-verif"], queryFn: () => getFn() });
  const [pending, setPending] = useState<{ storage_path: string; kind: DocKind; name: string }[]>([]);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [nextKind, setNextKind] = useState<DocKind>("id_card");
  const ref = useRef<HTMLInputElement>(null);

  const req = verQ.data?.request;
  const status = req?.status;

  async function uploadDoc(file: File, kind: DocKind) {
    if (!user) return;
    const pre = validateUpload("document", { size: file.size, type: file.type });
    if (!pre.ok) {
      toast.error(pre.message);
      return false;
    }
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      const up = await supabase.storage.from("chef-documents").upload(path, file, { contentType: file.type });
      if (up.error) throw up.error;
      setPending((p) => [...p, { storage_path: path, kind, name: file.name }]);
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore upload");
      return false;
    }
  }

  const submitMut = useMutation({
    mutationFn: () =>
      submitFn({
        data: {
          documents: pending.map((p) => ({ storage_path: p.storage_path, kind: p.kind })),
          notes: notes || undefined,
        },
      }),
    onSuccess: () => {
      toast.success("Richiesta di verifica inviata. Ti rispondiamo entro 24-48 ore.");
      setPending([]); setNotes("");
      qc.invalidateQueries({ queryKey: ["my-verif"] });
      qc.invalidateQueries({ queryKey: ["my-onboarding"] });
      qc.invalidateQueries({ queryKey: ["my-chef"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="container mx-auto max-w-3xl px-4 py-10">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">Dashboard</p>
      <h1 className="mt-1 font-display text-4xl inline-flex items-center gap-2">
        <ShieldCheck className="h-7 w-7 text-blue-600" /> Verifica Chef
      </h1>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        Ottieni il badge <strong>Verificato</strong> caricando i documenti che attestano la tua identità e qualifiche. La revisione richiede in media 24-48 ore.
      </p>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        Il badge Verificato aumenta la fiducia dei clienti ed è richiesto per pubblicare annunci.
      </p>

      {status === "pending" && (
        <Alert tone="info" icon={Clock} title="Richiesta in revisione">
          La tua ultima richiesta è stata inviata il {new Date(req!.submitted_at).toLocaleDateString("it-IT")} ed è in attesa di approvazione. Puoi comunque aggiungere altri documenti qui sotto.
        </Alert>
      )}
      {status === "approved" && (
        <Alert tone="success" icon={CheckCircle2} title="Sei un Chef Verificato">
          La tua richiesta è stata approvata. Il badge è visibile sul tuo profilo pubblico.
        </Alert>
      )}
      {status === "rejected" && (
        <Alert tone="error" icon={AlertCircle} title="Richiesta rifiutata">
          {req?.notes ?? "Documenti non sufficienti. Puoi inviarne di nuovi."}
        </Alert>
      )}

      {status !== "approved" && (
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl">Carica i tuoi documenti</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Formati accettati: PDF, JPG, PNG. I documenti sono privati e visibili solo allo staff. Seleziona il tipo prima di caricare ed evita di classificare tutto come "documento d'identità".
          </p>
          <p className="mt-3 text-sm text-blue-600">
            La verifica serve a confermare la tua identità e aumentare la fiducia dei clienti. Non viene richiesta la Partita IVA.
          </p>

          <div className="mt-5 space-y-3">
            {pending.map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3">
                <div className="flex min-w-0 items-center gap-3">
                  <ShieldCheck className="h-4 w-4 text-blue-600" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{p.name}</p>
                    <div className="mt-1 w-56">
                      <Select
                        value={p.kind}
                        onValueChange={(v) => {
                          const next = v as DocKind;
                          setPending((arr) => arr.map((x, k) => (k === i ? { ...x, kind: next } : x)));
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.entries(KIND_LABEL) as [DocKind, string][]).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => setPending((a) => a.filter((_, k) => k !== i))}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="sm:w-64">
              <Label className="mb-1 block text-xs">Tipo del prossimo documento</Label>
              <Select value={nextKind} onValueChange={(v) => setNextKind(v as DocKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.entries(KIND_LABEL) as [DocKind, string][]).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input
              ref={ref}
              type="file"
              multiple
              accept={acceptAttribute("document")}
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length === 0) return;
                setUploading(true);
                let ok = 0;
                for (const f of files) {
                  if (pending.length + ok >= 8) {
                    toast.error("Limite di 8 documenti raggiunto");
                    break;
                  }
                  const done = await uploadDoc(f, nextKind);
                  if (done) ok++;
                }
                setUploading(false);
                if (ref.current) ref.current.value = "";
                if (ok > 0) toast.success(`${ok} documento/i pronti. Verifica il tipo e invia la richiesta.`);
              }}
            />
            <Button variant="outline" onClick={() => ref.current?.click()} disabled={uploading || pending.length >= 8}>
              {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
              Aggiungi documenti
            </Button>
          </div>

          <div className="mt-5">
            <Label>Note per lo staff (facoltative)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Es. La P.IVA è intestata a... il diploma è di..."
              maxLength={1000}
            />
          </div>

          <Button
            onClick={() => submitMut.mutate()}
            disabled={submitMut.isPending || pending.length === 0}
            className="mt-5 bg-blue-600 text-white hover:bg-blue-700"
          >
            {submitMut.isPending ? "Invio…" : status === "pending" ? `Invia integrazione (${pending.length})` : `Invia richiesta (${pending.length})`}
          </Button>
        </div>
      )}
    </div>
  );
}

function Alert({
  tone, icon: Icon, title, children,
}: { tone: "info" | "success" | "error"; icon: React.ComponentType<{ className?: string }>; title: string; children: React.ReactNode }) {
  const cls =
    tone === "success" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200"
      : tone === "error" ? "border-red-500/40 bg-red-500/10 text-red-900 dark:text-red-200"
        : "border-blue-500/40 bg-blue-500/10 text-blue-900 dark:text-blue-200";
  return (
    <div className={`mt-6 rounded-xl border p-4 ${cls}`}>
      <p className="inline-flex items-center gap-2 font-semibold"><Icon className="h-4 w-4" /> {title}</p>
      <p className="mt-1 text-sm opacity-90">{children}</p>
    </div>
  );
}